import type { User } from "@supabase/supabase-js";
import { supabase } from "../supabase/client";

const USERNAME_PATTERN = /^[a-z0-9][a-z0-9._-]{2,31}$/i;
const USERNAME_DOMAIN = "accounts.lusky.app";

export type ChildGender = "girl" | "boy" | "unspecified";
export type SchoolStage = "kindergarten_small" | "kindergarten_middle" | "kindergarten_large" | "preschool" | "primary_1" | "";

export interface RegistrationProfile {
  nickname: string;
  gender: ChildGender;
  birthDate?: string;
  schoolStage: SchoolStage;
}

export interface ChildProfileDetails {
  id: string;
  nickname: string;
  avatar_storage_key: string | null;
  gender: ChildGender;
  birth_date: string | null;
  school_stage: Exclude<SchoolStage, ""> | null;
}

function friendlyAuthError(error: { message?: string; code?: string } | null, action: "login" | "register") {
  const message = String(error?.message || "").toLowerCase();
  const code = String(error?.code || "").toLowerCase();
  if (message.includes("invalid login credentials")) return new Error("用户名或密码不正确，请检查后再试。");
  if (message.includes("email not confirmed")) return new Error("账号尚未启用，请联系管理员完成确认。");
  if (message.includes("signups not allowed") || code === "signup_disabled") return new Error("注册通道尚未开启，请先在 Supabase 开启新用户注册。");
  if (message.includes("already registered") || code === "user_already_exists") return new Error("这个用户名已经被使用，请换一个试试。");
  if (message.includes("failed to fetch") || message.includes("network")) return new Error("网络连接不稳定，请检查网络后再试。");
  return new Error(error?.message || (action === "login" ? "登录没有完成，请稍后再试。" : "注册没有完成，请稍后再试。"));
}

export function usernameToEmail(value: string) {
  const normalized = value.trim().toLowerCase();
  if (normalized.includes("@")) return normalized;
  if (!USERNAME_PATTERN.test(normalized)) throw new Error("用户名请使用 3–32 位英文、数字、点、下划线或短横线。");
  return `${normalized}@${USERNAME_DOMAIN}`;
}

export async function ensureLearningProfile(user: User, childName = "小小探索师") {
  if (!supabase) throw new Error("学习记录服务暂不可用。");
  const displayName = String(user.user_metadata?.username || "试用家长").slice(0, 32);
  const parent = await supabase.from("parent_accounts").upsert({ id: user.id, display_name: displayName }, { onConflict: "id", ignoreDuplicates: true });
  if (parent.error) throw parent.error;
  const existing = await supabase.from("child_profiles").select("id,nickname,avatar_storage_key").eq("parent_id", user.id).order("created_at").limit(1).maybeSingle();
  if (existing.error) throw existing.error;
  if (existing.data) return existing.data;
  const created = await supabase.from("child_profiles").insert({ parent_id: user.id, nickname: childName.slice(0, 32), locale: "zh-CN" }).select("id,nickname,avatar_storage_key").single();
  if (created.error) throw created.error;
  return created.data;
}

export async function getPrivateAvatarUrl(storageKey?: string | null) {
  if (!supabase || !storageKey) return null;
  const result = await supabase.storage.from("child-media").createSignedUrl(storageKey, 3600);
  return result.error ? null : result.data.signedUrl;
}

export async function updateChildNickname(childId: string, nickname: string) {
  if (!supabase) throw new Error("学习记录服务暂不可用。");
  const next = nickname.trim();
  if (!next || next.length > 32) throw new Error("昵称需要填写，并且不能超过 32 个字。");
  const result = await supabase.from("child_profiles").update({ nickname: next, updated_at: new Date().toISOString() }).eq("id", childId).select("id,nickname,avatar_storage_key").single();
  if (result.error) throw result.error;
  await recordAccountEvent(childId, "profile.nickname_updated");
  return result.data;
}

export async function getChildProfileDetails(childId: string) {
  if (!supabase) throw new Error("学习记录服务暂不可用。");
  const result = await supabase.from("child_profiles").select("id,nickname,avatar_storage_key,gender,birth_date,school_stage").eq("id", childId).single();
  if (result.error) throw result.error;
  return result.data as ChildProfileDetails;
}

export async function updateChildProfileDetails(childId: string, profile: RegistrationProfile) {
  if (!supabase) throw new Error("学习记录服务暂不可用。");
  const nickname = profile.nickname.trim();
  if (!nickname || nickname.length > 32) throw new Error("昵称需要填写，并且不能超过 32 个字。");
  if (profile.birthDate && profile.birthDate > new Date().toISOString().slice(0, 10)) throw new Error("出生日期不能晚于今天。");
  const result = await supabase.from("child_profiles").update({
    nickname,
    gender: profile.gender,
    birth_date: profile.birthDate || null,
    school_stage: profile.schoolStage || null,
    updated_at: new Date().toISOString()
  }).eq("id", childId).select("id,nickname,avatar_storage_key,gender,birth_date,school_stage").single();
  if (result.error) throw result.error;
  await recordAccountEvent(childId, "profile.details_updated", {
    gender: profile.gender,
    school_stage: profile.schoolStage || null,
    has_birth_date: Boolean(profile.birthDate)
  });
  return result.data as ChildProfileDetails;
}

export async function uploadPrivateAvatar(userId: string, childId: string, file: File, previousKey?: string | null) {
  if (!supabase) throw new Error("学习记录服务暂不可用。");
  if (!file.type.startsWith("image/")) throw new Error("请选择图片文件。");
  if (file.size > 3 * 1024 * 1024) throw new Error("头像请控制在 3MB 以内。");
  const extension = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
  let oldKey = previousKey;
  if (typeof oldKey === "undefined") {
    const current = await supabase.from("child_profiles").select("avatar_storage_key").eq("id", childId).single();
    oldKey = current.data?.avatar_storage_key ?? null;
  }
  const storageKey = `${userId}/${childId}/avatar-${Date.now()}.${extension}`;
  const uploaded = await supabase.storage.from("child-media").upload(storageKey, file, { cacheControl: "3600", upsert: false, contentType: file.type });
  if (uploaded.error) throw uploaded.error;
  const updated = await supabase.from("child_profiles").update({ avatar_storage_key: storageKey, updated_at: new Date().toISOString() }).eq("id", childId);
  if (updated.error) { await supabase.storage.from("child-media").remove([storageKey]); throw updated.error; }
  if (oldKey && oldKey !== storageKey) await supabase.storage.from("child-media").remove([oldKey]);
  await recordAccountEvent(childId, "profile.avatar_updated", { content_type: file.type, byte_size: file.size });
  return { storageKey, signedUrl: await getPrivateAvatarUrl(storageKey) };
}

export async function recordAccountEvent(childId: string, eventType: string, payload: Record<string, unknown> = {}) {
  if (!supabase) return;
  await supabase.from("learning_events").insert({ child_id: childId, attempt_id: null, client_event_id: crypto.randomUUID(), event_type: eventType, payload, occurred_at: new Date().toISOString() });
}

export async function signInWithUsername(username: string, password: string) {
  if (!supabase) throw new Error("学习记录服务暂不可用。");
  if (password.length < 6) throw new Error("请输入至少 6 位密码。");
  const result = await supabase.auth.signInWithPassword({ email: usernameToEmail(username), password });
  if (result.error || !result.data.user) throw friendlyAuthError(result.error, "login");
  const child = await ensureLearningProfile(result.data.user);
  await recordAccountEvent(child.id, "account.login", { method: "username_password" });
  return { user: result.data.user, child };
}

export async function registerWithUsername(username: string, password: string, profile: RegistrationProfile) {
  if (!supabase) throw new Error("学习记录服务暂不可用。");
  if (password.length < 8) throw new Error("密码至少需要 8 位。");
  const nickname = profile.nickname.trim();
  if (!nickname || nickname.length > 32) throw new Error("昵称需要填写，并且不能超过 32 个字。");
  if (profile.birthDate && profile.birthDate > new Date().toISOString().slice(0, 10)) throw new Error("出生日期不能晚于今天。");
  const result = await supabase.auth.signUp({ email: usernameToEmail(username), password, options: { data: { username: username.trim().toLowerCase(), account_type: "trial" } } });
  if (result.error || !result.data.user) throw friendlyAuthError(result.error, "register");
  if (!result.data.session) throw new Error("账号已创建，但无法自动登录。请在 Supabase 关闭 Confirm Email 后再使用用户名注册。");
  const child = await ensureLearningProfile(result.data.user, nickname);
  const updated = await supabase.from("child_profiles").update({ nickname, gender: profile.gender, birth_date: profile.birthDate || null, school_stage: profile.schoolStage || null, onboarding_completed_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq("id", child.id).select("id,nickname,avatar_storage_key").single();
  if (updated.error) throw updated.error;
  await recordAccountEvent(child.id, "account.trial_registered", { method: "username_password", gender: profile.gender, school_stage: profile.schoolStage || null, has_birth_date: Boolean(profile.birthDate) });
  await recordAccountEvent(child.id, "profile.onboarding_completed");
  return { child: updated.data };
}